// context/FontScaleContext.js
// Preferensi ukuran font global per user. Nilai ditulis sebagai font-size pada
// <html>, sehingga SELURUH utility Tailwind ikut menskala — bukan hanya teks.
//
// PENTING: tailwind.config.js tidak meng-override `fontSize` maupun `spacing`,
// jadi keduanya memakai skala rem bawaan Tailwind. Konsekuensinya padding,
// gap, dan lebar tetap (p-4, gap-2, w-64) IKUT membesar. Efeknya menyerupai
// zoom browser, bukan sekadar teks membesar. Itu disengaja: menskalakan teks
// saja akan membuat baris berdesakan di dalam kotak yang tidak ikut tumbuh.
//
// Breakpoint Tailwind (sm/md/lg/xl) berbasis px dan TIDAK ikut menskala. Karena
// itu tangga di bawah sengaja berhenti di 18px. Menambah langkah 20px+ harus
// didahului uji layout pada halaman terpadat (AI Configuration, Sidebar,
// tabel SAP) — bukan ditebak.
//
// Pola penyimpanan sengaja dibuat identik dengan context/ThemeContext.js:
//   localStorage (perangkat)  +  users.font_scale (lintas perangkat)
// Kalau salah satu berubah, samakan keduanya.
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'app.fontScale';

// Default berlaku untuk perangkat yang BELUM pernah memilih (localStorage kosong
// dan users.font_scale masih NULL) — termasuk halaman login, karena di sana
// belum ada user. 16px sama dengan default browser, jadi tampilan lama tidak
// berubah bagi siapa pun yang tidak menyentuh setelan ini.
const DEFAULT_SCALE = 'md';

// px sengaja disimpan sebagai angka (bukan string "14px") agar bisa dipakai
// langsung sebagai ukuran pratinjau "Aa" di Header tanpa parsing.
export const FONT_SCALES = {
  sm: { labelKey: 'fontScale.sm', px: 14 },
  md: { labelKey: 'fontScale.md', px: 16 },
  lg: { labelKey: 'fontScale.lg', px: 18 },
};

// Kalau daftar di atas berubah, ganti JUGA:
//   1. public/index.html  — skrip pra-render (mencegah kedip ukuran)
//   2. backend/server.js  — konstanta FONT_SCALES pada PUT /api/auth/font-scale
// Ketidakcocokan dengan (2) gejalanya identik dengan sukses: tampilan berubah,
// tetapi nilainya tidak pernah tersimpan per-user. Persis jebakan 'amber' pada
// preset tema dulu.
export function applyFontScale(key) {
  const scale = FONT_SCALES[key] || FONT_SCALES[DEFAULT_SCALE];
  document.documentElement.style.fontSize = `${scale.px}px`;
}

const FontScaleContext = createContext(null);

export function FontScaleProvider({ children }) {
  const [fontScale, setFontScaleState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return FONT_SCALES[saved] ? saved : DEFAULT_SCALE;
    } catch (_) {
      return DEFAULT_SCALE;
    }
  });

  // Terapkan saat mount dan setiap kali berubah. Skrip di index.html sudah
  // menerapkan nilai yang sama sebelum render pertama; pemanggilan di sini
  // menangani perubahan sesudahnya dan hidrasi dari server.
  useEffect(() => { applyFontScale(fontScale); }, [fontScale]);

  const setFontScale = useCallback((key) => {
    if (!FONT_SCALES[key]) return;
    setFontScaleState(key);
    try { localStorage.setItem(STORAGE_KEY, key); } catch (_) {}
  }, []);

  // Dipakai saat hidrasi dari server (users.font_scale). Tidak memicu penulisan
  // balik ke API — hanya menyelaraskan state lokal dengan nilai tersimpan.
  // Nilai null/tidak dikenal diabaikan, artinya user belum pernah memilih dan
  // preferensi lokal (atau default) tetap dipakai.
  const hydrateFontScale = useCallback((key) => {
    if (!key || !FONT_SCALES[key]) return;
    setFontScaleState(prev => (prev === key ? prev : key));
    try { localStorage.setItem(STORAGE_KEY, key); } catch (_) {}
  }, []);

  return (
    <FontScaleContext.Provider
      value={{ fontScale, setFontScale, hydrateFontScale, scales: FONT_SCALES }}
    >
      {children}
    </FontScaleContext.Provider>
  );
}

export function useFontScale() {
  const ctx = useContext(FontScaleContext);
  if (!ctx) throw new Error('useFontScale harus dipakai di dalam <FontScaleProvider>');
  return ctx;
}
